import React, { useCallback } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import { IBackendCartEntries, IHistoricalOrder, IPicker } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { IGetUserOrdersQueryVariables } from 'generated/graphql-gateway';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'state/launchdarkly/flags';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { LoyaltyReward } from 'state/loyalty/types';
import { IPendingReorder } from 'state/order/types';
import { isOfferType } from 'state/order/utils';
import { CartEntryType } from 'utils/cart/types';
import { normalizeCartEntries } from 'utils/minimal-cart';

import { useEnableUnifiedMenu } from './use-enable-unified-menu';
import useFoodware from './use-foodware';
import { useLoyaltyRewardsList } from './use-loyalty-rewards-list';
import { useUserOrders } from './use-user-orders';

function isComboMarker({ name, lineId }: { name?: string; lineId?: string }): boolean {
  if (!name) {
    // Loyalty markers have name null and lineId===loyalty
    // Other items might not have name, so we assume those aren't markers
    return lineId === 'loyalty';
  }
  return ['combo sides', 'combo meals', 'combo item'].includes(name.toLowerCase().trim());
}

/**
 * Updates a cart reward cart entry
 *
 * @param {IBackendCartEntries} cartEntry - The original cart entry.
 * @returns {IBackendCartEntries} The modified cart entry.
 */
function maybeExtractRewardBenefitEntry(cartEntry: IBackendCartEntries): IBackendCartEntries {
  const hasValidChildren = Array.isArray(cartEntry.children) && cartEntry.children.length > 0;

  // Reward items don't share same structure as ALC items.
  // Rewards items will always have 3 children.
  // 1st one is comboItemMarkerEntry ,
  // 2nd is comboSideMarkerEntry (used for NCR) ,
  // 3rd is rewardBenefitEntry which represents actual reward benefit comboSlot
  // which has a children and that children is the actual reward item.
  if (cartEntry.type === CartEntryType.comboSlot && hasValidChildren) {
    const foundEntry = cartEntry.children!.find(
      entry => entry.name && entry.name.toLowerCase() !== 'combo sides'
    );

    if (foundEntry) {
      return {
        ...foundEntry,
        url: `/menu/${foundEntry.type.toLowerCase()}-${foundEntry.sanityId}`,
      };
    }
  }

  if (hasValidChildren) {
    for (const entry of cartEntry.children!) {
      try {
        return maybeExtractRewardBenefitEntry(entry);
      } catch (_) {}
    }
  }

  throw new Error('No valid reward entry found');
}

/**
 * Updates a cart entry based on a loyalty reward.
 *
 * @param {IBackendCartEntries} cartEntry - The original cart entry.
 * @param {LoyaltyReward} reward - The loyalty reward.
 * @returns {IBackendCartEntries} The modified cart entry.
 */
function maybeCreateCartEntryFromReward(
  cartEntry: IBackendCartEntries,
  reward: LoyaltyReward
): IBackendCartEntries {
  const incentive = reward.incentives?.[0];

  if (!incentive) {
    return cartEntry;
  }

  if (incentive._type === MenuObjectTypes.PICKER) {
    const userSelection = cartEntry.children?.find(child => child.lineId === 'loyalty');

    if (!userSelection) {
      return cartEntry;
    }

    const pickerOption = ((incentive as unknown) as IPicker).options.find(
      option => option.option._id === userSelection.sanityId
    );

    if (!pickerOption) {
      return cartEntry;
    }

    const benefitType =
      pickerOption.option._type === 'combo' ? CartEntryType.combo : CartEntryType.item;

    return {
      ...cartEntry,
      type: benefitType,
      sanityId: userSelection.sanityId,
    };
  }

  if (incentive._type === 'combo' || incentive._type === 'item') {
    return {
      ...cartEntry,
      sanityId: incentive._id,
      type: incentive._type === 'combo' ? CartEntryType.combo : CartEntryType.item,
    };
  }

  return cartEntry;
}

export const useReorderableOrders = (
  variables: IGetUserOrdersQueryVariables = {},
  fetchPolicy: WatchQueryFetchPolicy = 'no-cache'
) => {
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { allRewards } = useLoyaltyRewardsList();

  const allRewardsMap = React.useMemo(() => {
    const dictionary: Record<string, LoyaltyReward> = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);

  // Temporary flag to disable reordering of items/combos based on Sanity IDs.
  // Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  /*
   * Returns `true` if the item is not eligible for reordering.
   */
  const isCartEntryReorderable = useCallback(
    (entry: IBackendCartEntries): boolean => {
      const isExcludedFromReordering = (sanityIdsExcludedFromReordering ?? []).includes(
        entry.sanityId
      );

      return (
        !isComboMarker(entry) &&
        !isExcludedFromReordering &&
        !entry.isDonation &&
        !entry.isExtra &&
        !isFoodware(entry)
      );
    },
    [sanityIdsExcludedFromReordering, isFoodware]
  );

  /**
   * Creates cart entries from an offer by spreading its children, excluding specific types.
   *
   * @param {IBackendCartEntries} entry - The entry representing an offer or reward in the cart.
   * @returns {IBackendCartEntries[]} An array of cart entries created from the offer, excluding specific types.
   */
  const maybeCreateCartEntriesFromOffer = useCallback(
    (entry: IBackendCartEntries): IBackendCartEntries[] => {
      const mappedCartEntries = [];

      for (const rootLevelCartEntry of entry.children ?? []) {
        const isComboSlot = rootLevelCartEntry.type === CartEntryType.comboSlot;

        if (isComboSlot) {
          // Add all valid comboSlotOptions
          for (const comboSlotCartEntry of rootLevelCartEntry.children ?? []) {
            if (isCartEntryReorderable(comboSlotCartEntry)) {
              mappedCartEntries.push({
                ...comboSlotCartEntry,
                url: `/menu/${comboSlotCartEntry.type.toLowerCase()}-${
                  comboSlotCartEntry.sanityId
                }`,
              });
            }
          }

          continue;
        }

        // Regular root level entries should be added as is (generally these are offer's mainItem)
        if (isCartEntryReorderable(rootLevelCartEntry)) {
          mappedCartEntries.push({
            ...rootLevelCartEntry,
            url: `/menu/${rootLevelCartEntry.type.toLowerCase()}-${rootLevelCartEntry.sanityId}`,
          });
        }
      }

      return mappedCartEntries;
    },
    [isCartEntryReorderable]
  );

  const {
    orders,
    error,
    loading,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
  } = useUserOrders(variables, fetchPolicy);

  const makeOrderReorderable = useCallback(
    <T extends IPendingReorder>(order: T): T => {
      const mappedReorderCartEntries: IBackendCartEntries[] = [];

      for (const entry of order?.cart?.cartEntries ?? []) {
        if (!isCartEntryReorderable(entry)) {
          continue;
        }

        let mappedEntries: IBackendCartEntries[] = [];

        // Offers or Rewards
        if (isOfferType(entry.type as CartEntryType)) {
          // Checks if a reorder cart entry is a reward,
          // then replaces the cart entry with the reward benefit item.
          const reward = allRewardsMap?.[entry.sanityId];

          if (reward) {
            const rewardCartEntry = maybeCreateCartEntryFromReward(entry, reward);
            mappedEntries = [maybeExtractRewardBenefitEntry(rewardCartEntry)];
          } else {
            // If it's not a reward, it's an Offer
            // Offer can't be re-applied, so we spread all its children instead
            mappedEntries = maybeCreateCartEntriesFromOffer(entry);
          }
        } else {
          // Regular entries are preserved as is
          mappedEntries = [
            { ...entry, url: `/menu/${entry.type.toLowerCase()}-${entry.sanityId}` },
          ];
        }

        const entries = isUnifiedMenuEnabled ? mappedEntries : normalizeCartEntries(mappedEntries);

        mappedReorderCartEntries.push(...entries);
      }

      return {
        ...order,
        cart: {
          ...order.cart,
          reorderCartEntries: mappedReorderCartEntries,
        },
      };
    },
    [allRewardsMap, isUnifiedMenuEnabled, isCartEntryReorderable, maybeCreateCartEntriesFromOffer]
  );

  const reorderableOrders = React.useMemo(
    () =>
      orders.reduce((ordersAccum: IHistoricalOrder[], order) => {
        const reorderableOrder = makeOrderReorderable(order);

        if (reorderableOrder.cart.reorderCartEntries?.length) {
          ordersAccum.push(reorderableOrder);
        }

        return ordersAccum;
      }, []),
    [orders, makeOrderReorderable]
  );

  return {
    error,
    loading,
    orders,
    reorderableOrders,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
    makeOrderReorderable,
    isCartEntryReorderable,
  };
};
